<script lang="ts">
  import { onMount } from "svelte";
  import type { Api, PageSnapshot } from "../../lib/api";
  import { Icon, Button } from "@price-check/ui";
  import { Eye, AlertTriangle, Info, ArrowLeft } from "lucide";
  import Card from "../../lib/ui/Card.svelte";
  import Pill from "../../lib/ui/Pill.svelte";
  import Banner from "../../lib/ui/Banner.svelte";
  import Spinner from "../../lib/ui/Spinner.svelte";
  import EmptyState from "../../lib/ui/EmptyState.svelte";
  import { loginLabel, loginTone, snapshotWarn } from "../../lib/snapshot-status";
  import { kindLabel } from "../../lib/snapshot-groups";

  let {
    api,
    vendorId,
    vendorName,
    onBack,
    onView,
  }: {
    api: Api;
    vendorId: string;
    vendorName?: string;
    onBack: () => void;
    onView: (snapshotId: string) => void;
  } = $props();

  let snaps = $state<PageSnapshot[]>([]);
  let loading = $state(true);
  let error = $state("");

  // A direct link lands here with no group loaded, so the vendor's display name has
  // to be recoverable from the captures themselves.
  const title = $derived(vendorName || snaps.find((s) => s.vendor_name)?.vendor_name || vendorId);

  async function load() {
    loading = true;
    error = "";
    try {
      snaps = await api.listSnapshots({ vendorId, limit: 200 });
    } catch (e) {
      error = e instanceof Error ? e.message : "failed to load";
    } finally {
      loading = false;
    }
  }

  onMount(load);

  function when(iso: string): string {
    return new Date(iso).toLocaleString();
  }
</script>

<Card pad={false}>
  <header class="flex flex-wrap items-start gap-3 border-b border-border px-4.5 py-4">
    <div>
      <h3 class="m-0 text-md font-bold">
        {title}
        <span class="ml-1 font-mono text-sm font-normal text-muted">{vendorId}</span>
      </h3>
      <p class="mt-0.75 mb-0 text-sm text-muted">
        Every page captured for this supplier, newest first.
      </p>
    </div>
    <Button variant="outline" size="sm" class="ml-auto" onclick={onBack}>
      <Icon icon={ArrowLeft} size={14} /> All suppliers
    </Button>
  </header>

  <div class="p-4.5">
    {#if error}<Banner tone="danger">{error}</Banner>{/if}

    {#if loading}
      <Spinner label="Loading snapshots…" />
    {:else if snaps.length === 0}
      <EmptyState
        icon="▢"
        title="No snapshots for this supplier"
        hint="Captured landing + results pages will appear here as users run lookups."
      />
    {:else}
      <ul class="m-0 flex list-none flex-col gap-2 p-0">
        {#each snaps as s (s.id)}
          {@const warn = snapshotWarn(s)}
          <li class="rounded-lg border border-border bg-surface p-3">
            <div class="flex flex-wrap items-center gap-2">
              <Pill tone="indigo">{kindLabel(s.page_kind)}</Pill>
              <Pill tone={loginTone(s.login_state)}>{loginLabel(s.login_state)}</Pill>
              <span class="text-xs text-muted">{when(s.created_at)}</span>
              {#if s.user_email}<span class="text-xs text-muted">· {s.user_email}</span>{/if}
              <Button
                variant="outline"
                size="sm"
                class="ml-auto"
                aria-label="View {kindLabel(s.page_kind)} capture of {title}"
                onclick={() => onView(s.id)}
              >
                <Icon icon={Eye} size={14} /> View
              </Button>
            </div>
            <div class="mt-1.5 truncate text-xs text-muted" title={s.url}>{s.url}</div>
            {#if warn}
              <div
                class="mt-2 flex items-start gap-1.5 text-xs {warn.tone === 'danger'
                  ? 'text-danger'
                  : 'text-muted'}"
              >
                <Icon icon={warn.tone === "danger" ? AlertTriangle : Info} size={13} />
                <span>{warn.text}</span>
              </div>
            {/if}
          </li>
        {/each}
      </ul>
    {/if}
  </div>
</Card>
